'use client'
import type { UseLanguageSwitcherReturnType } from '@/components/language-switcher/language-switcher.types'
import type { SelectOption } from '@pearlconvert/ui/components/select/select.types'

import Cookies from 'js-cookie'
import { useLocale } from 'next-intl'
import { useRouter } from 'next/navigation'
import { useCallback, useMemo } from 'react'

import { warmUpCustomerCache } from '@/client/api/customer'
import { MAESTRO_LANG_COOKIE } from '@/utils/i18n.utils'
import { useMaestroSession } from '@maestro/commerce-blocks/providers/maestro-session/maestro-session.context'
import { theme } from '@maestro/local/theme'

export const useLanguageSwitcher = (): UseLanguageSwitcherReturnType => {
  const router = useRouter()
  const currentLocale = useLocale()
  const { session } = useMaestroSession()

  const isLoggedIn = useMemo(() => {
    return !!session
  }, [session])

  const options: SelectOption[] = useMemo(
    () =>
      theme().site.supportedLocales.map(({ code, label }) => {
        return {
          value: code,
          label,
        }
      }),
    []
  )

  const initialSelectedOption = useMemo(
    () =>
      options.find((option) => {
        return option.value === currentLocale
      }),
    [options, currentLocale]
  )

  const handleLanguageChange = useCallback(
    (option: SelectOption) => {
      const currentLanguage = Cookies.get(MAESTRO_LANG_COOKIE)

      if (currentLanguage === option.value) {
        return
      }

      Cookies.set(MAESTRO_LANG_COOKIE, option.value, {
        path: '/',
        expires: 365,
        sameSite: 'lax',
      })

      // if user logged in call warm up customer cache endpoint
      if (isLoggedIn) {
        void warmUpCustomerCache()
      }

      router.refresh()
    },
    [router, isLoggedIn]
  )

  return { options, initialSelectedOption, handleLanguageChange }
}
